import { validators } from './validators/validators';
import { dateAndTime } from '../inputFieldSet/dateTimePicker/dateAndTime';

export default class SelFP {
    constructor(props) {
        this.fields = props.fields;
        this.setLanguage(props.language);
    }

    initStates() {
        return {
            formWasValidated: false,
            fieldValues: this.getDefaultValues(),
            errors: this.getEmptyFieldNames(),
        }
    }

    setLanguage(language) {
        this.language = language;
    }

    getField(fieldName) {
        return this.fields.find(field => (field.name === fieldName));
    }

    getEmptyFieldNames() {
        const result = {};

        this.fields.forEach(field => {
            result[field.name] = '';
        });

        return result;
    }

    getDefaultValues() {
        const defaultValues = {};
        this.fields.forEach(field => {
            if ('initialValue' in field) {
                defaultValues[field.name] = field.initialValue;
            }
        })
        return defaultValues;
    }

    arrayOfChangedFields(fpStates, savedValues) {
        const stringifiedSavedValues = this.stringifyValues(fpStates, savedValues);
        const currentValues = this.stringifyValues(fpStates);

        const changedValues = Object.keys(stringifiedSavedValues)
            .filter(e => {
                if (((stringifiedSavedValues[e] || '') != (currentValues[e] || ''))) {
                    return true
                }
            })

        const newValues = Object.keys(currentValues)
            .filter(e => (!Object.keys(stringifiedSavedValues).includes(e)  && fpStates.fieldValues[e]));

        return [...changedValues, ...newValues];
    }

    isSaved(fpStates, savedValues) {
        if (!fpStates?.fieldValues?.ID) {
            return false;
        }

        return ((this.arrayOfChangedFields(fpStates, savedValues)).length === 0);
    }

    stringifyValues(fpStates, valuesForStringify) {
        const values = (valuesForStringify) ? valuesForStringify : fpStates.fieldValues;
        const dataNotNull = this.removeNulls(values);
        const dateFields = Object.keys(dataNotNull).filter(fieldName => (this.getField(fieldName)?.type === 'date' || this.getField(fieldName)?.type === 'dateTime'));
        const dateValues = Object.fromEntries(dateFields.map(fieldName => {
            const dateDbFormat = dateAndTime.getDateDbFormat(dataNotNull[fieldName]);
            return [fieldName, dateDbFormat];
        }));

        const data = {
            ...dataNotNull,
            ...dateValues,
        };

        return data;
    }

    setValues(fpStates, setFpStates, data) {
        setFpStates(previousState => {
            const newState = {
                ...previousState,
                fieldValues: { ...this.getDefaultValues(), ...this.getDataInternalFormat(data) },
                errors: this.getEmptyFieldNames()
            }
            return newState
        })
    }

    getDataInternalFormat(data) {
        const dateTimeFields = Object.keys(data).filter(fieldName => (this.getField(fieldName)?.type === 'dateTime'));
        const dateFields = Object.keys(data).filter(fieldName => (this.getField(fieldName)?.type === 'date'));

        return {
            ...data,
            //converting date values...
            ...Object.fromEntries(dateFields.map(fieldName => {
                const dateInternalFormat = dateAndTime.getDateInternalFormat(data[fieldName]);
                return [fieldName, dateInternalFormat]
            }
            )),
            //converting dateTime values...
            ...Object.fromEntries(dateTimeFields.map(fieldName => {
                const dateInternalFormat = dateAndTime.getDateTimeInternalFormat(data[fieldName], '+00:00');
                return [fieldName, dateInternalFormat]
            }
            )),
        }
    }

    removeNulls(jsonData) {
        const result = {};
        Object.keys(jsonData).forEach(k => {
            if (jsonData[k] !== null && jsonData[k] !== undefined && jsonData[k] !== '') {
                result[k] = jsonData[k];
            }
        })

        return result;
    }

    clearAllCustomValidity() {
        this.fields.forEach(field => {
            field.reference?.current?.setCustomValidity('');
        });
    }

    clearAllErrors(fpStates, setFpStates) {
        setFpStates(previousStates => ({
            ...previousStates,
            errors: this.getEmptyFieldNames()
        }));

        this.clearAllCustomValidity();
    }

    clearAllFields(fpStates, setFpStates) {
        setFpStates(previousStates => {
            const emptyFieldValues = { ...this.getEmptyFieldNames(), ...this.getDefaultValues() }

            return {
                ...previousStates,
                fieldValues: emptyFieldValues,
                errors: this.getEmptyFieldNames(),
            }
        });

        this.clearAllErrors(fpStates, setFpStates);
    }

    setFieldError(fieldName, errorText, setFpStates) {
        const field = this.getField(fieldName)

        if (field) {
            setFpStates((previousStates) => ({
                ...previousStates,
                errors: { ...previousStates.errors, [fieldName]: errorText }
            }));
            field.reference?.current?.setCustomValidity(errorText);
        }
    }

    async setFieldValue(fieldName, value, setFpStates) {
        setFpStates((previousStates) => {
            const newValues = previousStates.fieldValues;
            if (value === undefined || value === null) {
                delete newValues[fieldName];
            } else {
                newValues[fieldName] = value;
            }
            return {
                ...previousStates,
                fieldValues: { ...newValues, },
                errors: { ...previousStates.errors, [fieldName]: '' }
            }
        });
    }

    validateField(fieldName, fpStates, setFpStates) {
        const field = this.getField(fieldName)

        if (!field) {
            return true;
        }

        const value = fpStates.fieldValues[fieldName];

        this.setFieldError(fieldName, '', setFpStates);

        if (!Array.isArray(field.validators)) {
            return true;
        }

        let errorMessage;
        const invalid = field.validators?.find(validator => {
            errorMessage = validators.validate(field, validator, value, this.language, fpStates)
            return (errorMessage)
        })

        if (invalid) {
            if (errorMessage.substring(0, 4) === '###_') {
                const typeOfValidation = errorMessage.substring(4)
                errorMessage = validators.getErrorText(typeOfValidation, this.language)
            }
            this.setFieldError(fieldName, errorMessage, setFpStates);
            return false;
        }

        return true;
    }

    handleInputBlur(e, fpStates, setFpStates) {
        const fieldName = e.target.name;

        this.setFieldError(fieldName, '', setFpStates);
        this.validateField(fieldName, fpStates, setFpStates);
    }

    handleInputChange(e, fpStates, setFpStates) {
        let value;
        if (Array.isArray(e.target.value)) {
            value = JSON.stringify(e.target.value);
        } else if (typeof e.target.value === 'object') {
            value = JSON.stringify(e.target.value)
        } else if (e.target.type === 'checkbox') {
            value = e.target.checked;
        } else {
            value = e.target.value;
        }
        const fieldName = e.target.name;
        this.setFieldValue(fieldName, value, setFpStates)
    }

    isGroupValid(fpStates, setFpStates) {
        let result = true;
        this.fields.forEach(field => {
            const isFieldValid = this.validateField(field.name, fpStates, setFpStates);
            if (!isFieldValid) {
                result = false;
            }
        })
        return result;
    }
}
